import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Store } from '@ngrx/store';
import { FeatureConfigService, GlobalMessageService, GlobalMessageType, RoutingService, TranslationService } from '@spartacus/core';
import { ConfirmationMessageComponent, ConfirmationMessageData, MessageService } from '@spartacus/organization/administration/components';
import { ViewComponent } from '@spartacus/storefront';
import { Observable, Subject, Subscription } from 'rxjs';
import { switchMap, startWith, filter, tap, first } from 'rxjs/operators';
import { UigBranchesItemService } from '../../services/uig-branches-item.service';
import { UigBranchesListService } from '../../services/uig-branches-list.service';
import { deleteBranchReset, getUigStoreFeatureGroups } from '../../store/uig-branches.actions';
import { UigBranchesState } from '../../store/uig-branches.reducer';
import { deleteBranchSuccess, deleteBranchFailure, selectUigStoreFeatureGroups } from '../../store/uig-branches.selectors';
import { BranchForm, StoreLocatorFeatureGroup } from '../uig-branches.models';

@Component({
    selector: 'app-uig-branch-details',
    templateUrl: './uig-branch-details.component.html',
    styleUrls: ['./uig-branch-details.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'content-wrapper' },
    providers: [MessageService],
    standalone: false
})
export class UigBranchDetailsComponent implements OnInit, OnDestroy {

  protected confirmation: Subject<ConfirmationMessageData>;
  protected subscription = new Subscription();
  days: String[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  i18nRoot: string = "branchDetails";
  storeFeatures: StoreLocatorFeatureGroup[];
  @ViewChild(ViewComponent, { read: ViewComponent }) view: ViewComponent;
  branchToBeDeleted: string;
  deleteSuccess: Subscription;
  deleteFailure: Subscription;
  isInEditMode$ = this.itemService.isInEditMode$;

  item$: Observable<BranchForm> = this.itemService.key$.pipe(
    switchMap((name) => this.itemService.load(name)),
    startWith({})
  );

  deleteBranchSuccess$: Observable<boolean> = this.store.select(
    deleteBranchSuccess
  );

  deleteBranchFailure$: Observable<boolean> = this.store.select(
    deleteBranchFailure
  );

  storeFeatureGroups$: Observable<StoreLocatorFeatureGroup[]> =
    this.store.select(selectUigStoreFeatureGroups);

  constructor(
    protected itemService: UigBranchesItemService,
    protected listService: UigBranchesListService,
    protected translationService: TranslationService,
    protected globalMessageService: GlobalMessageService,
    protected routingService: RoutingService,
    protected store: Store<UigBranchesState>,
    protected messageService: MessageService<ConfirmationMessageData>,
    protected featureConfigService: FeatureConfigService,
    protected uigBranchesListService: UigBranchesListService
  ) {
    this.store.dispatch(getUigStoreFeatureGroups());
  }

  ngOnInit(): void {
    this.handleDeletionSuccess();
    this.handleDeletionError();
  }

  closeView(event: MouseEvent) {
    event.stopPropagation();
    this.view.toggle(true);

    setTimeout(() => {
      (event.target as HTMLElement)?.parentElement.click();
    }, 500);

    return false;
  }

  delete(name: string) {
    this.confirmation = this.messageService.add({
      messageTitle: {
        key: this.i18nRoot + '.messages.deleteTitle',
      },
      message: {
        key: this.i18nRoot + '.messages.deleteMessage',
      },
      confirm: {
        key: this.i18nRoot + '.buttons.confirm',
      },
      cancel: {
        key: this.i18nRoot + '.buttons.cancel'
      },
      component: ConfirmationMessageComponent,
    });

    this.subscription.add(
      this.confirmation.pipe(first()).subscribe((event) => {
        if (event.close) {
          this.confirmation = null;
        }
        if (event.confirm) {
          this.messageService.close(this.confirmation);
          this.itemService.deleteBranch(name);
          this.confirmation = null;
          this.branchToBeDeleted = name;
        }
      })
    );
  }

  handleDeletionSuccess() {
    this.deleteSuccess = this.deleteBranchSuccess$
      .pipe(
        filter((success) => success && success == true),
        tap(() => {
          this.translationService
            .translate(this.i18nRoot + '.messages.deleteSuccess', { branchName: this.branchToBeDeleted })
            .pipe(first())
            .subscribe((text) => {
              this.globalMessageService.add(text, GlobalMessageType.MSG_TYPE_CONFIRMATION);
            });
          this.uigBranchesListService.getList();
          this.store.dispatch(deleteBranchReset());
          this.routingService.go({ cxRoute: 'branches' });
        })
      )
      .subscribe();
  }

  handleDeletionError() {
    this.deleteFailure = this.deleteBranchFailure$
      .pipe(
        filter((failure) => failure && failure == true),
        tap(() => {
          this.translationService
            .translate('branchDetails.messages.deleteFailure')
            .pipe(first())
            .subscribe((text) => {
              this.globalMessageService.add(text, GlobalMessageType.MSG_TYPE_ERROR);
              this.store.dispatch(deleteBranchReset());
            });
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.deleteFailure.unsubscribe();
    this.deleteSuccess.unsubscribe();
  }
}
